/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useUsersList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing users list data and DataGrid state.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchUsers } from '../api/usersApi';
import { useUsersColumns } from './useUsersColumns';

export function useUsersList() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || t('users.defaultTenant');
  const availableColumns = useUsersColumns();

  const fetchUsersData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        return await fetchUsers(params, tenantSlug, user?.id);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('users.errors.loadFailed', 'Failed to load users');
        showError(message);
        throw error;
      }
    },
    [tenantSlug, user?.id, showError, t],
  );

  return useDataGrid({
    fetchData: fetchUsersData,
    initialColumns: availableColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'users',
  });
}

